import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetPayrollSettings,
  usePutPayrollSettings,
  getGetPayrollSettingsQueryKey,
} from '../../api/generated/arrayscapeGamingDashboardAPI';
import { PayrollSettings } from '../../api/generated/model';

const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const PayrollSettingsTab: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: settings, isLoading } = useGetPayrollSettings();
  const updateMutation = usePutPayrollSettings();

  const [formData, setFormData] = useState<PayrollSettings>({
    biweekly_start_day: 1,
    biweekly_reference_date: '',
  });
  const [originalData, setOriginalData] = useState<PayrollSettings>({
    biweekly_start_day: 1,
    biweekly_reference_date: '',
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (settings) {
      const data: PayrollSettings = {
        biweekly_start_day: settings.biweekly_start_day ?? 1,
        biweekly_reference_date: settings.biweekly_reference_date || '',
      };
      setFormData(data);
      setOriginalData(data);
    }
  }, [settings]);

  useEffect(() => {
    setHasChanges(JSON.stringify(formData) !== JSON.stringify(originalData));
  }, [formData, originalData]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await updateMutation.mutateAsync({
        data: {
          biweekly_start_day: formData.biweekly_start_day,
          biweekly_reference_date: formData.biweekly_reference_date || undefined,
        },
      });
      queryClient.invalidateQueries({ queryKey: getGetPayrollSettingsQueryKey() });
      setOriginalData(formData);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData(originalData);
    setError(null);
  };

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading settings...
      </div>
    );
  }

  return (
    <Card className="">
      <Card.Body>
        <h5 className="mb-3">Pay Period Settings</h5>
        <p className="text-muted mb-3">
          Configure the global pay period settings used for biweekly timesheet calculations.
        </p>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success">Settings saved successfully.</Alert>
        )}

        <Form onSubmit={(e) => e.preventDefault()}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Biweekly Start Day</Form.Label>
                <Form.Select
                  value={formData.biweekly_start_day ?? 1}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    biweekly_start_day: parseInt(e.target.value, 10),
                  }))}
                >
                  {dayNames.map((name, i) => (
                    <option key={i} value={i}>{name}</option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  The day of the week that each biweekly pay period starts on.
                </Form.Text>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Biweekly Reference Date</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.biweekly_reference_date || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    biweekly_reference_date: e.target.value,
                  }))}
                />
                <Form.Text className="text-muted">
                  An anchor date used to calculate biweekly pay period boundaries.
                  Choose a date that falls on the start day of a known pay period.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mt-4 pt-3 border-top">
            <Col className="d-flex justify-content-end">
              <Button
                variant="outline-secondary"
                className="me-2"
                disabled={!hasChanges}
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!hasChanges || saving}
                onClick={handleSave}
              >
                {saving ? (
                  <><Spinner animation="border" size="sm" className="me-1" />Saving...</>
                ) : (
                  'Save'
                )}
              </Button>
            </Col>
          </Row>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default PayrollSettingsTab;
